import {
  toSafeEditorLogRecord,
  type EditorLogCode,
  type SafeEditorErrorSummary,
} from '@heidi/editor/core';

/**
 * App-side narrowing for a `SafeEditorErrorSummary` on its way into a
 * `logger.*` payload (OpenSpec `monorepo-kit-editor` task 4.10 · app-side
 * rider).
 *
 * The frozen 4.10 envelope carries `name` through verbatim — it is a
 * constructor name by construction — but `Error#name` is WRITABLE, so
 * `error.name = <note text>` rides the envelope out. The package closes that
 * at its own boundary only: `createSafeEditorLogger` narrows the name before a
 * record reaches a host sink. No consumer supplies `onLog` yet, so the wired
 * path is the reporting callbacks (`onApplyFailure`,
 * `onSelectionRestoreFailure`, and the app-side `toSafeEditorErrorSummary`
 * catches), which hand the un-narrowed envelope straight to `logger.error`.
 *
 * This closes that half with the SAME narrowing, imported through the existing
 * `/core` door rather than reimplemented: a second identifier test app-side
 * would be a second thing to keep in step with the package's allowlist.
 */

/**
 * The package's narrowing is record-grained (`toSafeEditorLogRecord` is what
 * `/core` exports; the per-field `toSafeError` is private), so the summary
 * travels inside a record to reach it. `level` and `code` are only the
 * wrapper's required fields — neither is logged, and the code must be in the
 * package's closed set or the whole record is dropped fail-closed. Callers
 * pass the code the package itself emits for that failure path, so the wrapper
 * mirrors the record `onLog` would have carried had a host supplied one.
 */
const NARROWING_LEVEL = 'error';

/**
 * Fail-closed substitute for a summary the narrowing could not produce an
 * `error` field from — a nullish or non-object value arriving from untyped
 * code. Unreachable through a typed call; content-free if it is ever reached.
 */
const UNNARROWABLE_SUMMARY: SafeEditorErrorSummary = Object.freeze({
  name: 'UnnarrowableErrorSummary',
  classification: 'unknown',
});

/**
 * Narrow and freeze a safe summary for a logger payload. The returned envelope
 * keeps the envelope's two fields and its classification; only a `name` that
 * is not a JavaScript identifier is replaced (with the package's
 * `NonIdentifierErrorName`). Frozen because the payload outlives this call —
 * a transport, a serializer or a later handler must not be able to re-poison
 * the object the sink was handed.
 */
export const toLoggableEditorError = (
  error: SafeEditorErrorSummary,
  code: EditorLogCode
): Readonly<SafeEditorErrorSummary> => {
  const narrowed = toSafeEditorLogRecord({ level: NARROWING_LEVEL, code, error })?.error;
  return narrowed ? Object.freeze(narrowed) : UNNARROWABLE_SUMMARY;
};
